import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  Divider,
  FormControlLabel,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Field } from "formik";
import Switch from "@mui/material/Switch";
import Div from "@jumbo/shared/Div";
import { Label } from "app/pages/presentation/module/form/types";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { DSOK_EKASA_TYPES } from "app/utils/constants/settings";
import IKelpPosManagerPosPrinter from "./eKasa/IKelpPosManagerPosPrinter";
import PortosEkasa from "./eKasa/PortosEkasa";
import IKelpPosManagerSk from "./eKasa/IKelpPosManagerSk";
import DsokPrinter from "./eKasa/DsokPrinter";
import EetEkasa from "./eKasa/EetEkasa";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import jwtAuthAxios from "app/services/auth/jwtAuth";
import { getBEUrlForAdmin } from "app/services/api/management";

const EkasaSection = ({ device, setFieldValue, values, t }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();
  const [isTestingEkasa, setIsTestingEkasa] = useState(false);
  const [confirmEkasaTestOpen, setConfirmEkasaTestOpen] = useState(false);
  const [ekasaTestModalOpen, setEkasaTestModalOpen] = useState(false);
  const [ekasaTestResult, setEkasaTestResult] = useState(null);
  const [ekasaTestError, setEkasaTestError] = useState(null);
  const eKasaSupplier = values.customer.devices[device].ekasa?.eKasaType;
  let info;
  switch (eKasaSupplier) {
    case "portos_sk":
      info = t("pages.dsokDetails.infoTexts.eKasa.portos");
      break;
    case "fiskalPro_sk":
      info = t("pages.dsokDetails.infoTexts.eKasa.fiskalPro");
      break;
    case "iKelpPosManager_sk":
      info = t("pages.dsokDetails.infoTexts.eKasa.iKelpPosManager");
      break;
    case "iKelpPosManagerPosPrinter":
      info = t("pages.dsokDetails.infoTexts.eKasa.iKelpPosManagerPosPrinter");
      break;
    case "dsokPrinter":
      info = t("pages.dsokDetails.infoTexts.eKasa.dsokPrinter");
      break;
    case "eet_cz":
      info = t("pages.dsokDetails.infoTexts.eKasa.eetCz");
      break;
    default:
      info = "";
  }
  const eKasaEnabled =
    values.customer.devices[device].orderProcess?.enableEkasa;
  const ekasaHttpsEnabled = values.customer.devices[device].ekasa?.useHTTPS;
  const customerCode = values?.customer?.code;
  const beDomain =
    values?.customer?.beDomain ??
    values?.customer?.dsokSettings?.customer?.beDomain;

  const resolveDeviceId = () => {
    const currentDevice = values?.customer?.devices?.[device];
    const candidates = [
      currentDevice?.deviceId,
      currentDevice?.id,
      currentDevice?._id,
      currentDevice?.device_id,
      device,
    ];
    const found = candidates.find(
      (v) => v !== undefined && v !== null && String(v).trim().length > 0
    );
    return found;
  };

  const resetEkasaTestState = React.useCallback(() => {
    setEkasaTestModalOpen(false);
    setEkasaTestResult(null);
    setEkasaTestError(null);
  }, []);

  React.useEffect(() => {
    if (!ekasaTestModalOpen) return;

    const title = t("deviceTests.ekasa.resultTitle");

    const jsonBlock = (payload) => (
      <Box
        component="pre"
        sx={{
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          fontSize: 12,
          lineHeight: 1.4,
          whiteSpace: "pre",
          overflow: "auto",
          maxHeight: 420,
          bgcolor: "background.default",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 1.5,
          m: 0,
        }}
      >
        {typeof payload === "string"
          ? payload
          : JSON.stringify(payload ?? null, null, 2)}
      </Box>
    );

    const content = (
      <Box display="flex" flexDirection="column" gap={1.5}>
        {ekasaTestError ? (
          <>
            <Alert severity="error" variant="outlined">
              {t("deviceTests.ekasa.failed")}
            </Alert>
            {jsonBlock(ekasaTestError)}
          </>
        ) : (
          jsonBlock(ekasaTestResult)
        )}
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            size="small"
            sx={{ boxShadow: "none", height: "38px", width: "fit-content" }}
            onClick={() => {
              resetEkasaTestState();
              hideDialog();
            }}
          >
            {t("buttons.close")}
          </Button>
        </Box>
      </Box>
    );

    showDialog({
      variant: "default",
      title,
      content,
      onClose: resetEkasaTestState,
    });
  }, [
    ekasaTestModalOpen,
    ekasaTestError,
    ekasaTestResult,
    t,
    showDialog,
    hideDialog,
    resetEkasaTestState,
  ]);

  const handleTestEkasa = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const resolvedDeviceId = resolveDeviceId();
    if (!resolvedDeviceId) {
      enqueueSnackbar(
        t("deviceTests.ekasa.missingDeviceId"),
        { variant: "warning" }
      );
      return;
    }

    const deviceIdPayload = Number(resolvedDeviceId);
    if (!Number.isFinite(deviceIdPayload)) {
      enqueueSnackbar(
        t("deviceTests.ekasa.invalidDeviceId"),
        { variant: "warning" }
      );
      return;
    }

    if (!customerCode || String(customerCode).trim().length === 0) {
      enqueueSnackbar(
        t("deviceTests.ekasa.missingCustomerCode"),
        { variant: "warning" }
      );
      return;
    }

    setIsTestingEkasa(true);
    setEkasaTestResult(null);
    setEkasaTestError(null);

    try {
      jwtAuthAxios.defaults.baseURL = `${getBEUrlForAdmin(beDomain)}/`;
      const { data } = await jwtAuthAxios.post("/api/v1/test/device/ekasa", {
        deviceId: deviceIdPayload,
        customerCode: String(customerCode).trim(),
      });
      setEkasaTestResult(data);
      setEkasaTestError(null);
      setEkasaTestModalOpen(true);
    } catch (error) {
      const responseData = error?.response?.data;
      const message =
        responseData?.message ??
        responseData?.error ??
        error?.message ??
        t("deviceTests.ekasa.failed");
      const errorDetails =
        responseData != null
          ? { message, status: error?.response?.status, data: responseData }
          : { message };
      setEkasaTestResult(null);
      setEkasaTestError(errorDetails);
      setEkasaTestModalOpen(true);
    } finally {
      setIsTestingEkasa(false);
    }
  };

  const handleConfirmEkasaTest = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (isTestingEkasa || confirmEkasaTestOpen) return;

    setConfirmEkasaTestOpen(true);
    showDialog({
      variant: "confirm",
      title: t("deviceTests.ekasa.actionTitle"),
      content: (
        <Typography variant="body2">
          {t("deviceTests.ekasa.confirmText")}
        </Typography>
      ),
      onYes: async () => {
        setConfirmEkasaTestOpen(false);
        hideDialog();
        await handleTestEkasa();
      },
      onNo: () => {
        setConfirmEkasaTestOpen(false);
      },
    });
  };

  return (
    <>
      <Grid item xs={12} md={12} lg={12}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.ekasa")}
        </Typography>
        <Divider />
      </Grid>
      <Grid item xs={12} md={4} lg={4}>
        <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
          <FormControlLabel
            control={
              <Field
                name={`customer.devices.${device}.orderProcess.enableEkasa`}
                component={Switch}
                onChange={(e) =>
                  setFieldValue(
                    `customer.devices.${device}.orderProcess.enableEkasa`,
                    e.target.checked
                  )
                }
              />
            }
            label={
              <Label
                text={t("pages.dsokDetails.enableEkasa")}
                color="primary"
                helpText={t("pages.dsokDetails.enableEkasaHelp")}
              />
            }
            checked={values.customer.devices[device].orderProcess?.enableEkasa}
          />
        </Div>
      </Grid>
      {eKasaEnabled && (
        <>
          <Grid item xs={12} md={2} lg={2}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              <FormControlLabel
                control={
                  <Field
                    name={`customer.devices.${device}.ekasa.useHTTPS`}
                    component={Switch}
                    onChange={(e) =>
                      setFieldValue(
                        `customer.devices.${device}.ekasa.useHTTPS`,
                        e.target.checked
                      )
                    }
                  />
                }
                label={t("pages.dsokDetails.useHTTPS")}
                checked={ekasaHttpsEnabled}
              />
            </Div>
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <Div sx={{ mt: 1, mb: 1 }}>
              <JumboTextField
                fullWidth
                required
                select
                InputLabelProps={{ required: false }}
                name={`customer.devices.${device}.ekasa.eKasaType`}
                label={
                  <Label text={t("pages.dsokDetails.eKasaType")} required />
                }
              >
                {DSOK_EKASA_TYPES.map(({ code, name }) => {
                  return (
                    <MenuItem key={code} value={code}>
                      {t(`pages.dsokDetails.${name}`)}
                    </MenuItem>
                  );
                })}
              </JumboTextField>
            </Div>
          </Grid>
          <Grid item xs={12} md={1} lg={0.5}>
            <Div sx={{ mt: 2.25, mb: 1, mx: 0.5, display: "flex" }}>
              <Tooltip
                title={t("deviceTests.ekasa.actionTitle")}
                placement="top-start"
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={isTestingEkasa}
                    onClick={handleConfirmEkasaTest}
                    aria-label={t("deviceTests.ekasa.actionTitle")}
                    sx={{ height: 38, width: 38 }}
                  >
                    {isTestingEkasa ? (
                      <CircularProgress size={18} />
                    ) : (
                      <ReceiptLongIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Div>
          </Grid>
          {info && (
            <Grid item xs={12} md={12} lg={12}>
              <Alert variant="outlined" severity="info">
                {info}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={12} lg={12}></Grid>
          {eKasaSupplier && eKasaSupplier === "portos_sk" && (
            <PortosEkasa
              device={device}
              setFieldValue={setFieldValue}
              values={values}
              t={t}
            />
          )}
          {eKasaSupplier && eKasaSupplier === "iKelpPosManager_sk" && (
            <IKelpPosManagerSk
              device={device}
              setFieldValue={setFieldValue}
              values={values}
              t={t}
            />
          )}
          {eKasaSupplier && eKasaSupplier === "iKelpPosManagerPosPrinter" && (
            <IKelpPosManagerPosPrinter
              device={device}
              setFieldValue={setFieldValue}
              values={values}
              t={t}
            />
          )}
          {eKasaSupplier && eKasaSupplier === "dsokPrinter" && (
            <DsokPrinter
              device={device}
              setFieldValue={setFieldValue}
              values={values}
              t={t}
            />
          )}
          {eKasaSupplier && eKasaSupplier === "eet_cz" && (
            <EetEkasa
              device={device}
              setFieldValue={setFieldValue}
              values={values}
              t={t}
            />
          )}
        </>
      )}
    </>
  );
};

export default EkasaSection;
